import { MESApiService } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import {
	type StraightenerParamGroup,
	type StraightenerParamOption,
	type StraightenerParamValueType,
	type StraightenerParameter,
} from "../parameterCatalog";

interface SpParamRow {
	Code: string;
	Name: string;
	GroupCode: StraightenerParamGroup;
	SortOrder: number;
	RollCount: number;
	HasMinMax: boolean;
	SendToPlc: boolean;
	IsRequired: boolean;
	IsReadOnly: boolean;
	ValueType: StraightenerParamValueType;
	IsProductDim: boolean;
	MinValue: number | null;
	MaxValue: number | null;
}

interface SpOptionRow {
	ParameterCode: string;
	Value: string;
	Label: string;
	NumericValue: number | null;
	SortOrder: number;
}

interface SpTagRow {
	ParameterCode: string;
	RollNumber: number;
	Scale: number;
	Tolerance: number | null;
	BitPosition: number | null;
}

export interface StraightenerParameterCatalog {
	parameters: StraightenerParameter[];
	/** Highest per-roll instance count in the catalog. */
	rollCount: number;
}

// Assembles the three GetStraightenerParameterCatalog result sets into the
// front-end parameter shape. Scale/tolerance come from the RollNumber 0 (or
// first) tag row — per-roll instances of one parameter share their scaling.
export const getStraightenerParameterCatalog =
	async (): Promise<StraightenerParameterCatalog> => {
		const api = new MESApiService();
		const resp = await api.callV2(
			"[MILL].[GetStraightenerParameterCatalog]",
			[],
		);
		if (!resp.ok) {
			throw new Error(resp.message || "Error fetching straightener catalog");
		}

		const paramRows = get(resp, "data.tables[0].rows", []) as SpParamRow[];
		const optionRows = get(resp, "data.tables[1].rows", []) as SpOptionRow[];
		const tagRows = get(resp, "data.tables[2].rows", []) as SpTagRow[];

		const optionsByCode = new Map<string, StraightenerParamOption[]>();
		for (const o of optionRows) {
			const list = optionsByCode.get(o.ParameterCode) ?? [];
			list.push({
				value: o.Value,
				label: o.Label,
				numeric: o.NumericValue ?? 0,
			});
			optionsByCode.set(o.ParameterCode, list);
		}

		const tagByCode = new Map<string, SpTagRow>();
		for (const t of tagRows) {
			const prev = tagByCode.get(t.ParameterCode);
			if (!prev || t.RollNumber < prev.RollNumber) {
				tagByCode.set(t.ParameterCode, t);
			}
		}

		const parameters: StraightenerParameter[] = paramRows.map((r) => {
			const tag = tagByCode.get(r.Code);
			return {
				code: r.Code,
				displayName: r.Name,
				group: r.GroupCode,
				sortOrder: r.SortOrder,
				isPerRoll: r.RollCount > 0,
				hasMinMax: !!r.HasMinMax,
				valueType: r.ValueType,
				scale: tag?.Scale ?? 1,
				tolerance: tag?.Tolerance ?? null,
				sendToPlc: !!r.SendToPlc,
				readOnly: !!r.IsReadOnly,
				options: optionsByCode.get(r.Code),
			};
		});

		const rollCount = Math.max(0, ...paramRows.map((r) => r.RollCount));
		return { parameters, rollCount };
	};
